import { useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import cx from 'classnames'
import prettyBytes from 'pretty-bytes'

import Alert from 'common/components/alert/Alert'
import Button from 'common/components/button'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import Loader from 'common/components/loaders/Loader'
import Modal from 'common/components/Modal'
import { allowTabCharacter } from 'common/components/textarea/Textarea'
import { CloseSmall } from 'common/icons'

import {
  buildReferenceFileAccept,
  EnabledReferenceFileTypes,
  formatEnabledReferenceFileTypesLabel,
  ReferenceFileType,
} from './helpers/detectReferenceFileType'
import { processReferenceFile } from './helpers/processReferenceFile'
import { EmlSelection } from './EmlImportTabContent'
import { ImageSelection } from './ImageImportTabContent'
import { PdfSelection } from './PdfImportTabContent'
import ReferencePreview from './ReferencePreview'

const MAX_HTML_LENGTH = 100000

export type ReferenceSource = 'html' | 'pdf' | 'image' | 'eml'

type EnabledReferenceTypes = EnabledReferenceFileTypes & {
  html: boolean
}

type Props = {
  isOpen: boolean
  currentDescription?: string
  enabledTypes: EnabledReferenceTypes
  onClose: () => void
  onImportHtml: (html: string) => void
  onImportPdf: (text: string) => void
  onImportImage: (dataUri: string) => void
  onImportEml: (html: string) => void
}

const ImportContentModal = ({
  isOpen,
  currentDescription,
  enabledTypes,
  onClose,
  onImportHtml,
  onImportPdf,
  onImportImage,
  onImportEml,
}: Props) => {
  const [html, setHtml] = useState('')
  const [pdfSelection, setPdfSelection] = useState<PdfSelection | null>(null)
  const [imageSelection, setImageSelection] = useState<ImageSelection | null>(
    null
  )
  const [emlSelection, setEmlSelection] = useState<EmlSelection | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false)
  const fileRequestIdRef = useRef(0)

  const enabledFileTypes = useMemo(
    () => ({
      pdf: enabledTypes.pdf,
      image: enabledTypes.image,
      eml: enabledTypes.eml,
    }),
    [enabledTypes.pdf, enabledTypes.image, enabledTypes.eml]
  )

  const canChooseFile =
    enabledFileTypes.pdf || enabledFileTypes.image || enabledFileTypes.eml
  const canPaste = enabledTypes.html
  const fileTypesLabel = formatEnabledReferenceFileTypesLabel(enabledFileTypes)
  const accept = buildReferenceFileAccept(enabledFileTypes)

  const hasExistingContent = !!currentDescription?.trim()
  const activeSource: ReferenceSource | null = pdfSelection
    ? 'pdf'
    : imageSelection
      ? 'image'
      : emlSelection
        ? 'eml'
        : html.trim()
          ? 'html'
          : null

  const revokeImagePreview = () => {
    if (imageSelection?.previewUrl) {
      URL.revokeObjectURL(imageSelection.previewUrl)
    }
  }

  const clearFileSelections = () => {
    setPdfSelection(null)
    revokeImagePreview()
    setImageSelection(null)
    setEmlSelection(null)
  }

  const invalidateInFlightFileWork = () => {
    fileRequestIdRef.current += 1
  }

  const resetAll = () => {
    invalidateInFlightFileWork()
    setHtml('')
    clearFileSelections()
    setFileError(null)
  }

  const handleClose = () => {
    resetAll()
    onClose()
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }
    resetAll()
    // Reset only when the modal opens — not on every render of helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const setProcessingSelection = (kind: ReferenceFileType, file: File) => {
    setHtml('')
    setFileError(null)
    revokeImagePreview()
    setPdfSelection(null)
    setImageSelection(null)
    setEmlSelection(null)

    if (kind === 'pdf') {
      setPdfSelection({
        file,
        thumbnailUrl: null,
        extractedText: null,
        isProcessing: true,
        error: null,
      })
      return
    }
    if (kind === 'image') {
      setImageSelection({
        file,
        previewUrl: null,
        dataUri: null,
        isProcessing: true,
        error: null,
      })
      return
    }
    setEmlSelection({
      file,
      subject: null,
      html: null,
      isProcessing: true,
      error: null,
    })
  }

  const handleFileChosen = async (file: File) => {
    const requestId = ++fileRequestIdRef.current
    setHtml('')
    const result = await processReferenceFile(
      file,
      enabledFileTypes,
      setProcessingSelection
    )

    if (requestId !== fileRequestIdRef.current) {
      return
    }

    if (!result.ok) {
      clearFileSelections()
      setFileError(result.error)
      return
    }

    if (result.value.kind === 'pdf') {
      setPdfSelection(result.value.selection)
      return
    }
    if (result.value.kind === 'image') {
      setImageSelection(result.value.selection)
      return
    }
    setEmlSelection(result.value.selection)
  }

  const isBusy =
    !!pdfSelection?.isProcessing ||
    !!imageSelection?.isProcessing ||
    !!emlSelection?.isProcessing

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple: false,
    noClick: isBusy,
    noDrag: isBusy,
    noKeyboard: isBusy,
    disabled: !canChooseFile,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        clearFileSelections()
        setHtml('')
        setFileError(
          fileRejections[0].errors[0]?.message ??
            'This file could not be accepted.'
        )
        return
      }
      const file = acceptedFiles[0]
      if (file) {
        void handleFileChosen(file)
      }
    },
  })

  const handleHtmlChange = (value: string) => {
    if (value.trim()) {
      invalidateInFlightFileWork()
      clearFileSelections()
      setFileError(null)
    }
    setHtml(value)
  }

  const selectedFile =
    pdfSelection?.file ?? imageSelection?.file ?? emlSelection?.file ?? null

  const isImportDisabled =
    activeSource === 'html'
      ? !html.trim()
      : activeSource === 'pdf'
        ? !pdfSelection?.extractedText
        : activeSource === 'image'
          ? !imageSelection?.dataUri
          : activeSource === 'eml'
            ? !emlSelection?.html
            : true

  const performImport = () => {
    if (activeSource === 'html') {
      onImportHtml(html)
    } else if (activeSource === 'pdf' && pdfSelection?.extractedText) {
      onImportPdf(pdfSelection.extractedText)
    } else if (activeSource === 'image' && imageSelection?.dataUri) {
      onImportImage(imageSelection.dataUri)
    } else if (activeSource === 'eml' && emlSelection?.html) {
      onImportEml(emlSelection.html)
    }
    resetAll()
  }

  const handleImportClick = () => {
    if (hasExistingContent) {
      setIsReplaceConfirmOpen(true)
      return
    }
    performImport()
  }

  const contentTypeLabel =
    activeSource === 'pdf'
      ? 'PDF'
      : activeSource === 'image'
        ? 'image'
        : activeSource === 'eml'
          ? 'email'
          : 'HTML'

  return (
    <>
      <Modal
        visible={isOpen && !isReplaceConfirmOpen}
        onClose={handleClose}
        title="Add a reference"
        size="large"
        // Warm cream tint for this modal only (via inline style — Modal.tsx
        // skips its default bg-white when a custom background is passed,
        // since this project's Tailwind `important: true` config means a
        // plain inline style can't otherwise win against that !important class).
        style={{ backgroundColor: '#fbf7f0' }}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isImportDisabled || isBusy}
              onClick={handleImportClick}
              data-testid="import-content-button"
            >
              {isBusy ? 'Extracting...' : 'Add reference'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col h-full min-h-0">
          <p className="text-base text-base-700 pb-4 shrink-0">
            Paste the source of an existing campaign, or choose a file. Jacquard
            works out the format.
          </p>

          <div className="flex gap-6 flex-1 min-h-0">
            <div className="flex-1 flex flex-col min-w-0 min-h-0 gap-4">
              {canChooseFile && (
                <div className="shrink-0">
                  <p className="text-sm font-medium text-base-700 pb-2">
                    Choose a file
                  </p>
                  <div
                    {...getRootProps()}
                    data-testid="reference-file-dropzone"
                    className={cx(
                      'flex items-center justify-between gap-3 rounded-sm px-4 py-3 outline-none transition-colors bg-gold-300/25',
                      {
                        'cursor-pointer': !isBusy,
                        'cursor-not-allowed': isBusy,
                      }
                    )}
                  >
                    <input {...getInputProps()} />
                    <div className="min-w-0">
                      {selectedFile ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="text-sm text-base-700 truncate"
                            data-testid="reference-file-name"
                          >
                            {selectedFile.name} (
                            {prettyBytes(selectedFile.size)})
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              clearFileSelections()
                              setFileError(null)
                            }}
                            data-testid="reference-file-remove-button"
                          >
                            <CloseSmall size={3} isDefaultColor={false} />
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-base-500">
                          {isDragActive
                            ? 'Drop your file here'
                            : fileTypesLabel || 'No file types enabled'}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      size="medium"
                      disabled={isBusy}
                      data-testid="reference-browse-button"
                    >
                      Browse
                    </Button>
                  </div>
                  {isBusy && (
                    <div className="pt-2">
                      <Loader size="small" />
                    </div>
                  )}
                </div>
              )}

              {fileError && (
                <Alert type="error" showIcon className="shrink-0">
                  {fileError}
                </Alert>
              )}

              {canChooseFile && canPaste && (
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex-1 border-t border-gold-400" />
                  <span className="text-xs font-medium uppercase tracking-wide text-coolGray-400">
                    OR
                  </span>
                  <div className="flex-1 border-t border-gold-400" />
                </div>
              )}

              {canPaste && (
                <div className="flex-1 flex flex-col min-h-0">
                  <p className="text-sm font-medium text-base-700 pb-2 shrink-0">
                    Paste content
                  </p>
                  <textarea
                    value={html}
                    onChange={(e) => handleHtmlChange(e.target.value)}
                    onKeyDown={allowTabCharacter}
                    placeholder="Paste your HTML..."
                    maxLength={MAX_HTML_LENGTH}
                    data-testid="paste-html-modal-textarea"
                    className="flex-1 min-h-[160px] w-full resize-none font-mono text-sm bg-white text-gold-700 placeholder:text-gold-500 border-1 border-gold-400 hover:border-gold-500 focus:border-base-700 rounded-sm px-4 py-3 outline-none"
                  />
                </div>
              )}
            </div>

            <div className="flex-1 flex flex-col min-w-0 min-h-0 border-l border-gold-400 pl-6">
              <ReferencePreview
                html={html}
                pdfSelection={pdfSelection}
                imageSelection={imageSelection}
                emlSelection={emlSelection}
              />
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        open={isReplaceConfirmOpen}
        title="Replace brief description"
        confirmationText={`Adding this ${contentTypeLabel} reference will generate a new brief description from it, replacing what's there now.`}
        confirmButtonText="Proceed"
        cancelButtonText="Cancel"
        confirmButtonVariant="primary"
        onCancel={() => setIsReplaceConfirmOpen(false)}
        onConfirm={() => {
          setIsReplaceConfirmOpen(false)
          performImport()
        }}
        style={{ backgroundColor: '#fbf7f0' }}
      />
    </>
  )
}

export default ImportContentModal
